// PinnedList — 사이드바 S1 섹션 (U40)
// pinned=true인 items를 최상단 "고정" 섹션으로 표시한다.
//
// 동작:
// - useItems({ pinned: 1, limit: 8 }) 로 최대 8개 고정 항목 fetch
// - 행 형식: 아이콘(타입별) + 이름(ellipsis) + 채워진 ★ 버튼
// - 클릭 → ?item=<id> URL 갱신
// - ★ 클릭 → 즐겨찾기 해제 (unpinItem mutation)
// - 드래그 순서 변경 (dnd-kit sortable, localStorage 저장)
// - 빈 상태: "별 아이콘 누르면 여기 고정됩니다" 텍스트
//
// 의존: lucide-react, @dnd-kit/sortable, useItems, useItemMutations

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  FileText,
  Globe,
  GripVertical,
  Key,
  Server,
  Star,
  Terminal,
} from 'lucide-react';
import { useItems, useItemMutations } from '../../hooks/useItems';
import type { ItemMeta, ItemType } from '../../api/items';

// ─── 상수 ──────────────────────────────────────────────

/** localStorage 키 — 고정 항목 순서 저장 */
const PINNED_ORDER_KEY = 'locker:pinned-order';

/** 최대 표시 개수 */
const MAX_PINNED = 8;

// ─── 순서 영속화 헬퍼 ──────────────────────────────────

/**
 * localStorage에 저장된 고정 항목 순서(id 배열)를 읽는다.
 * 파싱 실패 시 빈 배열 반환.
 */
export function loadPinnedOrder(): number[] {
  try {
    const raw = localStorage.getItem(PINNED_ORDER_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return (parsed as unknown[]).filter((v): v is number => typeof v === 'number');
  } catch {
    return [];
  }
}

/**
 * id 배열을 localStorage에 저장한다.
 */
export function savePinnedOrder(ids: number[]): void {
  try {
    localStorage.setItem(PINNED_ORDER_KEY, JSON.stringify(ids));
  } catch {
    // localStorage 쓰기 실패 무시 (Private 모드 등)
  }
}

/**
 * 서버 items 배열을 localStorage 순서에 맞게 정렬한다.
 * localStorage에 없는 항목은 뒤에 추가된다.
 */
export function applyPinnedOrder(items: ItemMeta[], order: number[]): ItemMeta[] {
  if (order.length === 0) return items;

  const byId = new Map(items.map((item) => [item.id, item]));
  const sorted: ItemMeta[] = [];

  // localStorage 순서대로 있는 것 먼저
  for (const id of order) {
    const item = byId.get(id);
    if (item) {
      sorted.push(item);
      byId.delete(id);
    }
  }

  // 순서에 없는 신규 항목은 뒤에 추가
  for (const item of byId.values()) {
    sorted.push(item);
  }

  return sorted;
}

// ─── 타입별 아이콘 매핑 ────────────────────────────────

/**
 * ItemType에 맞는 lucide 아이콘 컴포넌트를 반환한다.
 * 미지정 타입은 FileText(기본) 반환.
 */
export function getItemIcon(type: ItemType) {
  switch (type) {
    case 'credential_account':
      return Key;
    case 'credential_api_key':
      return Key;
    case 'credential_server':
      return Server;
    case 'credential_env':
      return Terminal;
    case 'credential_auth':
      return Key;
    case 'server':
      return Server;
    case 'domain':
      return Globe;
    case 'service':
      return Globe;
    default:
      return FileText;
  }
}

// ─── SortablePinnedRow ─────────────────────────────────

interface SortablePinnedRowProps {
  item: ItemMeta;
  /** 항목 클릭 콜백 */
  onSelect: (id: number) => void;
  /** 고정 해제 콜백 */
  onUnpin: (id: number) => void;
}

/**
 * 드래그 가능한 고정 항목 행.
 * 타입 아이콘 + 이름(ellipsis) + 채워진 별 버튼(클릭 시 즐겨찾기 해제).
 * 드래그 핸들은 hover 시에만 노출.
 */
function SortablePinnedRow({ item, onSelect, onUnpin }: SortablePinnedRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = getItemIcon(item.type);

  return (
    <li
      ref={setNodeRef}
      style={style}
      className={`pinned-list__row${isDragging ? ' pinned-list__row--dragging' : ''}`}
    >
      {/* 드래그 핸들 — hover 시에만 보임 */}
      <span
        className="pinned-list__grip"
        aria-hidden
        {...attributes}
        {...listeners}
      >
        <GripVertical size={12} />
      </span>

      {/* 타입 아이콘 + 이름 — 클릭 시 detail 패널 */}
      <button
        type="button"
        className="pinned-list__name-btn"
        onClick={() => onSelect(item.id)}
        aria-label={item.name}
        title={item.name}
      >
        <Icon size={14} aria-hidden className="pinned-list__type-icon" />
        <span className="pinned-list__name">{item.name}</span>
      </button>

      {/* 채워진 별 — 클릭 시 즐겨찾기 해제 (카드의 ★ 토글과 같은 의미) */}
      <button
        type="button"
        className="pinned-list__unpin-btn"
        aria-label={`${item.name} 즐겨찾기 해제`}
        title="즐겨찾기 해제"
        onClick={(e) => {
          e.stopPropagation();
          onUnpin(item.id);
        }}
      >
        <Star size={13} aria-hidden fill="currentColor" />
      </button>
    </li>
  );
}

// ─── PinnedList ────────────────────────────────────────

/**
 * 사이드바 S1 섹션 — 고정된 items를 최상단에 표시한다.
 * dnd-kit sortable로 순서를 변경하며, localStorage로 영속화한다.
 */
export function PinnedList() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // pinned=1 항목 fetch (최대 8개)
  const { data, isLoading } = useItems({ pinned: 1, limit: MAX_PINNED });
  const { unpinItem } = useItemMutations();

  // 렌더 순서 — localStorage + 서버 데이터 합성
  const serverItems: ItemMeta[] = data?.items ?? [];
  const [order, setOrder] = useState<number[]>(() => loadPinnedOrder());

  // 서버 데이터가 바뀌면 신규 항목을 order에 추가
  useEffect(() => {
    if (serverItems.length === 0) return;
    setOrder((prev) => {
      const prevSet = new Set(prev);
      const newIds = serverItems.map((i) => i.id).filter((id) => !prevSet.has(id));
      if (newIds.length === 0) return prev;
      const merged = [...prev, ...newIds];
      savePinnedOrder(merged);
      return merged;
    });
  }, [serverItems.map((i) => i.id).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // 서버 items를 order 기준으로 정렬
  const sortedItems = applyPinnedOrder(serverItems, order);
  const sortedIds = sortedItems.map((i) => i.id);

  // ─── dnd-kit 설정 ──────────────────────────────────

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setOrder((prev) => {
        const oldIdx = prev.indexOf(Number(active.id));
        const newIdx = prev.indexOf(Number(over.id));
        if (oldIdx === -1 || newIdx === -1) return prev;
        const next = arrayMove(prev, oldIdx, newIdx);
        savePinnedOrder(next);
        return next;
      });
    },
    [],
  );

  // ─── 이벤트 핸들러 ─────────────────────────────────

  const handleSelect = useCallback(
    (id: number) => {
      // ?item=<id> — 기존 파라미터 유지하면서 item 추가
      const next = new URLSearchParams(searchParams);
      next.set('item', String(id));
      navigate(`/?${next.toString()}`);
    },
    [navigate, searchParams],
  );

  const handleUnpin = useCallback(
    (id: number) => {
      unpinItem.mutate(id);
      // order에서도 제거
      setOrder((prev) => {
        const next = prev.filter((v) => v !== id);
        savePinnedOrder(next);
        return next;
      });
    },
    [unpinItem],
  );

  // ─── 렌더 ──────────────────────────────────────────

  return (
    <section className="sidebar__section" aria-label="고정 항목">
      <div className="sidebar__heading pinned-list__heading">
        <Star size={14} aria-hidden fill="currentColor" className="pinned-list__star-icon" />
        <span className="pinned-list__heading-label">즐겨찾기</span>
        {sortedItems.length > 0 && (
          <span className="pinned-list__count">{sortedItems.length}</span>
        )}
      </div>

      {/* 로딩 상태 */}
      {isLoading && (
        <div className="muted sidebar__hint">불러오는 중…</div>
      )}

      {/* 빈 상태 */}
      {!isLoading && sortedItems.length === 0 && (
        <div className="muted sidebar__hint pinned-list__empty">
          항목의 ☆를 누르면 여기에 추가됩니다
        </div>
      )}

      {/* 고정 항목 목록 */}
      {!isLoading && sortedItems.length > 0 && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={sortedIds} strategy={verticalListSortingStrategy}>
            <ul className="sidebar__list pinned-list__list" role="list" aria-label="고정된 항목 목록">
              {sortedItems.map((item) => (
                <SortablePinnedRow
                  key={item.id}
                  item={item}
                  onSelect={handleSelect}
                  onUnpin={handleUnpin}
                />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </section>
  );
}
